import { PROJECT_COLUMNS_OVERVIEW } from './queryColumns';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE ANTWORT AUF DAS PROJEKT-UPDATE OHNE DEN GANZEN BLOB
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `saveProjectToCloud` schreibt `projects.data` (~35 KB) und liess sich mit
 * `.select()` die GANZE Zeile zurückgeben — denselben Blob noch einmal. Die
 * Aufrufer brauchen davon (geprüft an allen Stellen, die den Rückgabewert
 * lesen): `id`, `updated_at` (neuer Anker), die Verknüpfungsspalten (Nr. 71,
 * `applyLinkColumns`), die übrigen Spalten der Projektliste (App spreizt die
 * Zeile hinein) und aus `data` genau EIN Stück, das der Server anders haben
 * kann als gesendet: das Protokoll `metadata.globalData.serviceLog`.
 *
 * Warum gerade das Protokoll: der Riegel `service_log_guard` (Nr. 44/66/74/79,
 * BEFORE UPDATE auf projects) hängt fehlende Arbeitszeilen wieder an und
 * streicht Grabstein-Zeilen — und das ist die EINZIGE Stelle im Schema, die
 * `new.data` verändert (alle Zuweisungen an `new.data` in supabase/*.sql
 * stehen in den vier Fassungen dieses Riegels und setzen nur
 * `{metadata,globalData,serviceLog}`). `App.handleQueueFlushed` übernimmt
 * genau dieses Protokoll nach dem Flush (Stufe 1b, 26051016: 43 Rettungen
 * derselben Zeile, weil der State die alte Kopie behielt).
 *
 * Also: die Spalten der Einzelübersicht ohne `data`, dazu das Protokoll per
 * JSON-Pfad; `data` wird aus dem GESENDETEN Blob gebaut und das Protokoll
 * des Servers eingesetzt. Inhaltlich derselbe Stand wie bisher (nur die
 * Schlüsselreihenfolge von jsonb fehlt).
 *
 * Kennt die Datenbank eine Spalte der Liste nicht (42703), scheitert die
 * GANZE Anweisung vor dem Schreiben — dann fährt der Aufrufer einmal mit
 * `.select()` nach und lässt die schmale Antwort für diese Sitzung bleiben
 * (`sperreSchmaleAntwort`).
 */

/** Alias für das Protokoll des Servers in der schmalen Antwort. */
export const PROTOKOLL_ALIAS = 'server_service_log';

/** Die Auswahl für das Projekt-Update im sparsamen Modus. */
export const schmaleProjektAuswahl = (): string => [
    ...PROJECT_COLUMNS_OVERVIEW.filter((spalte) => spalte !== 'data'),
    `${PROTOKOLL_ALIAS}:data->metadata->globalData->serviceLog`,
].join(', ');

let gesperrt = false;

/** Darf das Update seine Antwort schmal anfordern? Nach einem Spaltenfehler nicht mehr. */
export const schmaleAntwortMoeglich = (): boolean => !gesperrt;

/** Nach einem Spaltenfehler: für diese Sitzung wieder die volle Zeile. */
export const sperreSchmaleAntwort = (): void => { gesperrt = true; };

/** Nur für Tests. */
export const setzeSchmaleAntwortZurueck = (): void => { gesperrt = false; };

const istObjekt = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Baut aus der schmalen Antwort die Zeile, die der Aufrufer bisher bekam:
 * alle gelieferten Spalten, `data` = Kopie des gesendeten Blobs, darin das
 * Protokoll des Servers — wenn er eines geliefert hat. Liefert er keines
 * (null, kein Array), bleibt das gesendete stehen: der Riegel entfernt nie
 * das ganze Protokoll, ein fehlendes heisst „nichts zu übernehmen".
 */
export const zeileAusSchmalerAntwort = (
    antwort: Record<string, unknown>,
    gesendeterBlob: unknown,
): Record<string, unknown> => {
    const { [PROTOKOLL_ALIAS]: protokoll, ...spalten } = antwort;
    const data: Record<string, unknown> = istObjekt(gesendeterBlob)
        ? JSON.parse(JSON.stringify(gesendeterBlob))
        : {};
    if (Array.isArray(protokoll)) {
        if (!istObjekt(data.metadata)) data.metadata = {};
        const metadata = data.metadata as Record<string, unknown>;
        if (!istObjekt(metadata.globalData)) metadata.globalData = {};
        (metadata.globalData as Record<string, unknown>).serviceLog = protokoll;
    }
    return { ...spalten, data };
};
